import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/vue-query';
import type { CadFile } from '@cutr/constants/order';
import type { ManufacturingIssue } from '@cutr/constants/order-files';
import {
  setManufacturingIssueValidity,
  setManufacturingIssueVisibility,
} from '@/api/manufacturingIssues';
import { cancelListsWith, setCachedLists } from './orderFiles';

export type SetIssueVisibilityVariables = { ids: string[]; show: boolean };
export type SetIssueValidityVariables = { ids: string[]; isValid: boolean };

type IssueField = 'show' | 'isValid';

export function patchIssues(
  files: CadFile[],
  updated: ManufacturingIssue[],
  field: IssueField
) {
  const updatedById = new Map(updated.map((issue) => [issue.id, issue]));
  return files.map((file) => {
    const issues = file.cadFileManufacturingIssues;
    if (!issues?.some(({ id }) => updatedById.has(id))) return file;
    return {
      ...file,
      cadFileManufacturingIssues: issues.map((issue) => {
        const saved = updatedById.get(issue.id);
        return saved ? { ...issue, [field]: saved[field] } : issue;
      }),
    };
  });
}

async function patchCachedIssues(
  queryClient: QueryClient,
  updated: ManufacturingIssue[],
  field: IssueField
) {
  const fileIds = [...new Set(updated.map(({ cadFileId }) => cadFileId))];
  await Promise.all(fileIds.map((id) => cancelListsWith(queryClient, id)));
  fileIds.forEach((id) =>
    setCachedLists(queryClient, id, (files) =>
      patchIssues(files, updated, field)
    )
  );
}

export function useManufacturingIssueMutations() {
  const queryClient = useQueryClient();

  const visibility = useMutation({
    mutationFn: (variables: SetIssueVisibilityVariables) =>
      setManufacturingIssueVisibility(variables),
    onSuccess: (updated) => patchCachedIssues(queryClient, updated, 'show'),
  });

  const validity = useMutation({
    mutationFn: (variables: SetIssueValidityVariables) =>
      setManufacturingIssueValidity(variables),
    onSuccess: (updated) => patchCachedIssues(queryClient, updated, 'isValid'),
  });

  return {
    setIssueVisibility: (variables: SetIssueVisibilityVariables) =>
      visibility.mutateAsync(variables).then(() => undefined),
    setIssueValidity: (variables: SetIssueValidityVariables) =>
      validity.mutateAsync(variables).then(() => undefined),
  };
}
